import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { useAuth } from '../context/AuthContext';
import { getUserOrders, updateOrderStatus } from '../services/api';
import { formatPrice, formatDate } from '../utils/formatPrice';
import { toast } from 'react-toastify';

const statusColors = {
  Pending: { bg: '#fff3e0', color: '#e65100' },
  Accepted: { bg: '#e3f2fd', color: '#1565c0' },
  Shipped: { bg: '#e8f5e9', color: '#2e7d32' },
  Delivered: { bg: '#e8f5e9', color: '#2e7d32' },
  Completed: { bg: '#e8f5e9', color: '#2e7d32' },
  Cancelled: { bg: '#ffebee', color: '#c62828' },
  Rejected: { bg: '#ffebee', color: '#c62828' },
};

const Order = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchOrders = async () => {
      try {
        const userId = localStorage.getItem('UserId');
        const response = await getUserOrders(userId);
        if (response.data.data) {
          setOrders(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const openCancelDialog = (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setCancelDialog(true);
  };

  const handleCancelOrder = async () => {
    try {
      await updateOrderStatus({ id: cancelOrderId, Statusmsg: 'Cancelled', CancelReason: cancelReason });
      setOrders(orders.map(order =>
        order._id === cancelOrderId ? { ...order, Status: 'Cancelled', CancelReason: cancelReason } : order
      ));
      toast.success('Order cancelled successfully');
      setCancelDialog(false);
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  if (loading) {
    return <Loader message="Loading your orders..." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb items={[{ label: 'My Orders' }]} />

        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>
          My Orders
        </Typography>

        {orders.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {orders.map((order) => (
              <Paper
                key={order._id}
                elevation={0}
                sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order placed on {formatDate(order.created_at)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Order ID: {order._id.slice(-8).toUpperCase()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {order.Products?.length || 0} item{order.Products?.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    <Chip
                      label={order.Status}
                      sx={{
                        bgcolor: statusColors[order.Status]?.bg || '#f5f5f5',
                        color: statusColors[order.Status]?.color || '#666',
                        fontWeight: 'bold',
                      }}
                    />
                    {order.TotalAmount && (
                      <Typography variant="h6" color="#2e7d32" fontWeight="bold">
                        Total: Rs.{formatPrice(order.TotalAmount)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Display all products in the order */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {order.Products && order.Products.length > 0 ? (
                    order.Products.map((product, index) => (
                      <Grid container spacing={3} alignItems="center" key={product.ProductId?._id || index}>
                        <Grid item xs={12} sm={2}>
                          <Box
                            onClick={() => navigate(`/viewproduct/${product.ProductId?._id || product.ProductId}`)}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 2,
                              overflow: 'hidden',
                              bgcolor: '#f5f5f5',
                            }}
                          >
                            <img
                              src={product.ProductMainImgUrl}
                              alt={product.ProductName}
                              style={{
                                width: '100%',
                                height: 100,
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {product.ProductName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Quantity: {product.ProductQuantity} | Size: {product.ProductSize || 'N/A'}
                          </Typography>
                          {product.ProductColor && product.ProductColor !== '0' && (
                            <Typography variant="body2" color="text.secondary">
                              Color: {product.ProductColor}
                            </Typography>
                          )}
                          <Typography variant="h6" color="#2e7d32" sx={{ mt: 1 }}>
                            Rs.{formatPrice(product.ProductPrice * product.ProductQuantity)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                            {index === 0 && order.Status === 'Pending' && (
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => openCancelDialog(order._id)}
                              >
                                Cancel Order
                              </Button>
                            )}
                            {index === 0 && (order.Status === 'Shipped' || order.Status === 'Accepted') && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1565c0' }}>
                                <LocalShippingIcon />
                                <Typography variant="body2" fontWeight="bold">
                                  On the way
                                </Typography>
                              </Box>
                            )}
                            {index === 0 && order.Status === 'Delivered' && (
                              <Button
                                variant="outlined"
                                sx={{ borderColor: '#8B4513', color: '#8B4513' }}
                              >
                                Write a Review
                              </Button>
                            )}
                          </Box>
                        </Grid>
                        {index < order.Products.length - 1 && <Grid item xs={12}><Divider /></Grid>}
                      </Grid>
                    ))
                  ) : (
                    <Typography color="text.secondary">No products in this order</Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 3,
              bgcolor: '#fff',
              textAlign: 'center',
            }}
          >
            <ShoppingBagIcon sx={{ fontSize: 80, color: '#ddd', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No orders yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Start shopping to see your orders here
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/filter/All')}
              sx={{
                bgcolor: '#8B4513',
                '&:hover': { bgcolor: '#6d3610' },
                px: 4,
              }}
            >
              Start Shopping
            </Button>
          </Paper>
        )}
      </Container>

      {/* Cancel Reason Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#c62828' }}>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please let us know why you want to cancel this order:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Cancellation Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Changed my mind, found better price, ordered by mistake..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Keep Order</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelOrder}
            disabled={!cancelReason.trim()}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Order;
